/**
 * Fleet Slice for Wadakaroo
 *
 * Manages workshop-side fleet customer state:
 * - Paginated, searchable list of fleet customers
 * - Create + invite a new fleet customer (portal login)
 * - Resend / suspend / reactivate a fleet login
 *
 * Mirrors the async-thunk + slice conventions used in authSlice.js.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    listFleetCustomersApi,
    createFleetCustomerApi,
    resendFleetInviteApi,
    suspendFleetLoginApi,
    reactivateFleetLoginApi,
} from '../../services/fleetApi';

// ============================================
// Async Thunks
// ============================================

/**
 * Load a page of fleet customers. Accepts { page, size, search }.
 */
export const fetchFleetCustomers = createAsyncThunk(
    'fleet/fetchFleetCustomers',
    async ({ page = 0, size = 20, search = '', createdFrom, createdTo } = {}, { rejectWithValue }) => {
        try {
            // A search reaches any date; without one the server requires a month window.
            const data = await listFleetCustomersApi({
                page, size, search,
                createdFrom: search ? undefined : createdFrom,
                createdTo:   search ? undefined : createdTo,
            });
            return { data, page, size, search };
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to load fleet customers.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Create a fleet customer and send the portal invite.
 */
export const createFleetCustomer = createAsyncThunk(
    'fleet/createFleetCustomer',
    async (payload, { rejectWithValue }) => {
        try {
            return await createFleetCustomerApi(payload);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create fleet customer.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Re-send the onboarding email (fresh temporary password).
 */
export const resendFleetInvite = createAsyncThunk(
    'fleet/resendFleetInvite',
    async (customerUuid, { rejectWithValue }) => {
        try {
            return await resendFleetInviteApi(customerUuid);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to resend invite.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Suspend a fleet login.
 */
export const suspendFleetLogin = createAsyncThunk(
    'fleet/suspendFleetLogin',
    async (customerUuid, { rejectWithValue }) => {
        try {
            return await suspendFleetLoginApi(customerUuid);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to suspend login.';
            return rejectWithValue(message);
        }
    }
);

/**
 * Reactivate a suspended fleet login.
 */
export const reactivateFleetLogin = createAsyncThunk(
    'fleet/reactivateFleetLogin',
    async (customerUuid, { rejectWithValue }) => {
        try {
            return await reactivateFleetLoginApi(customerUuid);
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to reactivate login.';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // List
    items: [],
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    search: '',
    loading: false,
    error: null,

    // Create + invite
    creating: false,
    createError: null,

    // Row-level action in flight (resend/suspend/reactivate), keyed by customerUuid
    rowActionUuid: null,
    rowActionError: null,
};

const fleetSlice = createSlice({
    name: 'fleet',
    initialState,
    reducers: {
        setFleetSearch(state, action) {
            state.search = action.payload;
        },
        clearFleetErrors(state) {
            state.error = null;
            state.createError = null;
            state.rowActionError = null;
        },
    },
    extraReducers: (builder) => {
        // ---- List ----
        builder
            .addCase(fetchFleetCustomers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFleetCustomers.fulfilled, (state, action) => {
                const { data, page, size, search } = action.payload;
                state.loading = false;
                state.items = data.content || [];
                state.page = data.page ?? page;
                state.size = data.size ?? size;
                state.totalElements = data.totalElements ?? 0;
                state.totalPages = data.totalPages ?? 0;
                state.search = search;
            })
            .addCase(fetchFleetCustomers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load fleet customers.';
            });

        // ---- Create ----
        builder
            .addCase(createFleetCustomer.pending, (state) => {
                state.creating = true;
                state.createError = null;
            })
            .addCase(createFleetCustomer.fulfilled, (state) => {
                state.creating = false;
            })
            .addCase(createFleetCustomer.rejected, (state, action) => {
                state.creating = false;
                state.createError = action.payload || 'Failed to create fleet customer.';
            });

        // ---- Row actions (resend / suspend / reactivate) ----
        const rowPending = (state, action) => {
            state.rowActionUuid = action.meta.arg;
            state.rowActionError = null;
        };
        const rowSettled = (state) => {
            state.rowActionUuid = null;
        };
        const rowRejected = (state, action) => {
            state.rowActionUuid = null;
            state.rowActionError = action.payload || 'Action failed.';
        };

        builder
            .addCase(resendFleetInvite.pending, rowPending)
            .addCase(resendFleetInvite.fulfilled, rowSettled)
            .addCase(resendFleetInvite.rejected, rowRejected)
            .addCase(suspendFleetLogin.pending, rowPending)
            .addCase(suspendFleetLogin.fulfilled, rowSettled)
            .addCase(suspendFleetLogin.rejected, rowRejected)
            .addCase(reactivateFleetLogin.pending, rowPending)
            .addCase(reactivateFleetLogin.fulfilled, rowSettled)
            .addCase(reactivateFleetLogin.rejected, rowRejected);
    },
});

export const { setFleetSearch, clearFleetErrors } = fleetSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectFleetCustomers = (state) => state.fleet.items;
export const selectFleetLoading = (state) => state.fleet.loading;
export const selectFleetError = (state) => state.fleet.error;
export const selectFleetPage = (state) => state.fleet.page;
export const selectFleetSize = (state) => state.fleet.size;
export const selectFleetTotalPages = (state) => state.fleet.totalPages;
export const selectFleetTotalElements = (state) => state.fleet.totalElements;
export const selectFleetSearch = (state) => state.fleet.search;
export const selectFleetCreating = (state) => state.fleet.creating;
export const selectFleetCreateError = (state) => state.fleet.createError;
export const selectFleetRowActionUuid = (state) => state.fleet.rowActionUuid;

export default fleetSlice.reducer;